import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from "formik";
import * as Yup from "yup";
import { Editor } from '@tinymce/tinymce-react';
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import { ItemsService } from "../../../services/user/items.services";
import { CategoryService } from "../../../services/user/category.services";
import striptags from 'striptags';
import { ChildTypeEnum, INTER_STATE_TAX_GROUP_ENUM, INTRA_STATE_TAX_GROUP_ENUM, ItemTypeEnum, SubChildEnum } from "../../../utility";
import Select from 'react-select';
import { TaxGroupService } from "../../../services/user/taxgroups.services";

const AddItemsComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);
    const [isSubTypeSelected, setIsSubTypeSelected] = useState(false);
    const itemId = location.state?.itemId;
    const isEditMode = !!itemId;
    const [categories, setCategories] = useState([]);
    const [parentItemData, setParentItemData] = useState([]);
    const [intraStateTaxGroups, setIntraStateTaxGroups] = useState([]);
    const [interStateTaxGroups, setInterStateTaxGroups] = useState([]);

    const formik = useFormik({
        initialValues: {
            itemName: "",
            price: 0,
            itemCode: "",
            HSN_SACCode: "",
            searchKeyword: "",
            isActive: true,
            intraStateTaxGroupId: 0,
            interStateTaxGroupId: 0,
            itemDescription: "",
            termsAndCondition: "",
            itemtype: ItemTypeEnum.main,
            childType: ChildTypeEnum.standard,
            subchild: SubChildEnum.software,
            parentItemsId: [],
            categoryId: 0,
            itemImageFileName: "",
            itemImage: "",
        },
        validationSchema: Yup.object({
            itemName: Yup.string().required("Item Name is Required"),
            price: Yup.string().required("Item Name is Required"),
        }),

        onSubmit: async (values) => {
            try {
                // debugger
                const cleanedDescription = striptags(values.itemDescription);
                const cleanedtermsAndCondition = striptags(values.termsAndCondition);
                const validCategoryId = parseInt(values.categoryId, 10);
                const validParentItemsId = values.parentItemsId.length > 0 ? values.parentItemsId : []

                const updatedValues = {
                    ...values,
                    parentItemsId: values.parentItemsId ? validParentItemsId : [],
                    categoryId: values.categoryId ? validCategoryId : 0,
                    itemDescription: cleanedDescription || "",
                    termsAndCondition: cleanedtermsAndCondition || "",
                    itemImageFileName: values.itemImageFileName,
                    itemImage: values.itemImage,
                }

                if (isEditMode) {
                    await ItemsService.updateItemsById(itemId, updatedValues)
                        .then(response => {
                            if (response) {
                                navigate('/items')
                            }
                        })
                }
                else {
                    await ItemsService.addNewItems(updatedValues)
                        .then(response => {
                            if (response) {
                                navigate('/items')
                            }
                        })
                }
            }
            catch (error) {
                handleNotify('An Error while saving items..', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT)
            }
        },
    });

    const handleBackClick = () => {
        navigate('/items');
    };

    const fetchCategory = async () => {
        try {
            const response = await CategoryService.getAllCategory();
            if (response) {
                const sortedCategory = response.sort((a, b) => a.categoryId - b.categoryId);
                const categoryIdOptions = sortedCategory.map(category => ({
                    value: category.categoryId,
                    label: category.categoryName,
                }));
                if (!isSubTypeSelected && formik.values.itemtype === "main" && categoryIdOptions.length > 0) {
                    formik.setFieldValue("categoryId", categoryIdOptions[0].value);
                }
                setCategories(categoryIdOptions);
            }
            else {
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    useEffect(() => {
        fetchCategory();
    }, []);

    useEffect(() => {
        if (isEditMode) {
            const fetchItemsDetail = async () => {
                try {
                    const data = await ItemsService.getItemsById(itemId);
                    const fetchedItemType = data?.itemtype;
                    if (fetchedItemType === 'sub') {
                        setIsSubTypeSelected(true);
                    }
                    if (fetchedItemType === 'main') {
                        setIsSubTypeSelected(false);
                    }
                    formik.setValues({
                        itemId: data?.itemId,
                        itemName: data?.itemName,
                        price: data?.price,
                        itemCode: data?.itemCode,
                        HSN_SACCode: data?.HSN_SACCode,
                        searchKeyword: data?.searchKeyword,
                        isActive: data?.isActive,
                        intraStateTaxGroupId: data?.intraStateTaxGroupId,
                        interStateTaxGroupId: data?.interStateTaxGroupId,
                        itemDescription: data?.itemDescription,
                        termsAndCondition: data?.termsAndCondition,
                        itemtype: fetchedItemType,
                        childType: data?.childType,
                        subchild: data?.subchild,
                        parentItemsId: Array.isArray(data?.parentItemsId) ? (data?.parentItemsId).map(id => Number(id)) : [Number(data?.parentItemsId)],
                        categoryId: data?.categoryId,
                        itemImage: data?.itemImage,
                        itemImageFileName: data?.itemImageFileName,
                    });
                    if (data?.itemImageBase64) {
                        setPreviewImageUrl(`data:image/jpeg;base64,${data.itemImageBase64}`);
                    }
                }
                catch (error) {
                    console.error('Error fetching Items details', error)
                }
            }
            fetchItemsDetail()
        }
    }, []);

    const handleImageFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedImage(URL.createObjectURL(file));
            formik.setFieldValue('itemImage', file);
            formik.setFieldValue('itemImageFileName', file.name);

            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImageUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleItemTypeChange = (event) => {
        const selectedType = event.target.value;
        formik.setFieldValue('itemtype', selectedType);

        if (selectedType === ItemTypeEnum.main) {
            setIsSubTypeSelected(false);
            formik.setFieldValue('parentItemsId', []);
            formik.setFieldValue('childType', null);
            formik.setFieldValue('subchild', null);
            formik.setFieldValue('categoryId', categories[0].categoryId);
        }

        if (selectedType === ItemTypeEnum.sub) {
            setIsSubTypeSelected(true);
            formik.setFieldValue('categoryId', null);
            if (formik.values.childType === null) {
                formik.setFieldValue('childType', ChildTypeEnum.standard);
            }
            if (formik.values.subchild === null) {
                formik.setFieldValue('subchild', SubChildEnum.software);
            }
            formik.setFieldValue('parentItemsId', []);
        }
    };

    const fetchParentItemData = async () => {
        try {
            const response = await ItemsService.getAllItems();

            if (Array.isArray(response)) {
                const filteredItems = response.filter(item => item.itemtype === 'main');
                const sortedParentItem = filteredItems.sort((a, b) => a.itemId - b.itemId);
                const options = sortedParentItem.map(item => ({
                    value: item.itemId,
                    label: item.itemName,
                }));
                setParentItemData(options);
            } else {
                setParentItemData([]);
            }
        } catch (error) {
            console.error('Error fetching Items Data', error);
            setParentItemData([]);
        }
    };

    useEffect(() => {
        fetchParentItemData();
    }, []);

    const handleChange = (selectedOptions) => {
        formik.setFieldValue(
            'parentItemsId',
            selectedOptions ? selectedOptions.map((option) => option.value) : []
        );
    };

    const fetchTaxGroupData = async () => {
        const taxGroupResponse = await TaxGroupService.getAllTaxGroups();
        const interStateTaxGroup = taxGroupResponse.filter((taxGroup) => taxGroup.taxType === "Inter State");
        const intraStateTaxGroup = taxGroupResponse.filter((taxGroup) => taxGroup.taxType === "Intra State");
        const sortedInterStateTaxGroup = interStateTaxGroup.sort((a, b) => a.interStateTaxGroupId - b.interStateTaxGroupId);
        const sortedIntraStateTaxGroup = intraStateTaxGroup.sort((a, b) => a.intraStateTaxGroupId - b.intraStateTaxGroupId);
        const interStateTaxGroupOptions = sortedInterStateTaxGroup.map(taxGroup => ({
            value: taxGroup.taxGroupId,
            label: taxGroup.taxGroupName,
        }));

        const intraStateTaxGroupOptions = sortedIntraStateTaxGroup.map(taxGroup => ({
            value: taxGroup.taxGroupId,
            label: taxGroup.taxGroupName,
        }));

        setInterStateTaxGroups(interStateTaxGroupOptions);
        setIntraStateTaxGroups(intraStateTaxGroupOptions);

        if (!formik.values.interStateTaxGroupId && interStateTaxGroupOptions.length > 0) {
            formik.setFieldValue('interStateTaxGroupId', interStateTaxGroupOptions[0].value);
        }

        if (!formik.values.intraStateTaxGroupId && intraStateTaxGroupOptions.length > 0) {
            formik.setFieldValue('intraStateTaxGroupId', intraStateTaxGroupOptions[0].value);
        }
    };

    useEffect(() => {
        fetchTaxGroupData();
    }, [])

    return (
        <>
            <div className="page-wrapper m-0 p-4">
                <div className="page-content">
                    <div className="page-breadcrumb d-flex align-items-center mb-3">
                        <div
                            className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
                            onClick={handleBackClick}
                        >
                            Items</div>
                        <div className="ps-3">
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb mb-0 p-0">
                                    <li className="breadcrumb-item active" aria-current="page">
                                        {isEditMode ? 'Edit Items' : 'Add Items'}
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="ms-auto">
                            <button
                                type="button"
                                className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                                onClick={handleBackClick}
                            >
                                Back
                            </button>
                        </div>
                    </div>

                    <div className="row">
                        <form onSubmit={formik.handleSubmit} className="row mt-4">
                            <div className="card radius-10 border-top border-0 border-4 border-primary">
                                <div className="card-body p-5">
                                    <div className="row">
                                        <div className="col-12 col-lg-6 col-md-6 mb-3 border border-gray border-3 radius-10 p-4 m-3">
                                            <div className="row">
                                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                                    <label htmlFor="itemName" className="form-label d-flex">
                                                        ItemName <span className="text-danger">*</span>:
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="itemName"
                                                        name="itemName"
                                                        className={`form-control ${formik.touched.itemName && formik.errors.itemName ? 'is-invalid' : ''}`}
                                                        placeholder="Enter Item Name"
                                                        value={formik.values.itemName}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                    />
                                                    {formik.touched.itemName && formik.errors.itemName && (
                                                        <div className="invalid-feedback d-flex">{formik.errors.itemName}</div>
                                                    )}
                                                </div>
                                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                                    <label htmlFor="price" className="form-label d-flex">
                                                        Price <span className="text-danger">*</span>:
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="price"
                                                        name="price"
                                                        className={`form-control ${formik.touched.price && formik.errors.price ? 'is-invalid' : ''}`}
                                                        placeholder="Enter Price"
                                                        value={formik.values.price}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                    />
                                                    {formik.touched.price && formik.errors.price && (
                                                        <div className="invalid-feedback d-flex">{formik.errors.price}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                                    <label htmlFor="itemCode" className="form-label d-flex">
                                                        Item Code :
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="itemCode"
                                                        className="form-control"
                                                        placeholder="Enter Item Code"
                                                        value={formik.values.itemCode}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                    />
                                                </div>

                                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                                    <label htmlFor="HSN_SACCode" className="form-label d-flex">
                                                        HSN / SAC Code :
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="HSN_SACCode"
                                                        className="form-control"
                                                        placeholder="HSN_SACCode"
                                                        value={formik.values.HSN_SACCode}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                    />
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                                    <label htmlFor="searchKeyword" className="form-label d-flex">
                                                        Search Keyword :
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="searchKeyword"
                                                        className="form-control"
                                                        placeholder="searchKeyword"
                                                        value={formik.values.searchKeyword}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                    />
                                                </div>

                                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                                    <label htmlFor="intraStateTaxGroupId" className="form-label d-flex">
                                                        Intra State Tax group:
                                                    </label>
                                                    <div className="text-start">
                                                        <Select
                                                            id="intraStateTaxGroupId"
                                                            name="intraStateTaxGroupId"
                                                            options={intraStateTaxGroups}
                                                            value={intraStateTaxGroups.find(option => option.value === formik.values.intraStateTaxGroupId)}
                                                            onChange={(selectedOption) => formik.setFieldValue('intraStateTaxGroupId', selectedOption.value)}
                                                            menuPortalTarget={document.body}
                                                            placeholder="Select Intra State Tax Group "
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row d-flex">
                                                <div className="col-12 col-lg-6 col-md-4">
                                                    <label htmlFor="interStateTaxGroupId" className="form-label d-flex">
                                                        Inter State Tax group:
                                                    </label>
                                                    <div className="text-start">
                                                        <Select
                                                            id="interStateTaxGroupId"
                                                            name="interStateTaxGroupId"
                                                            options={interStateTaxGroups}
                                                            value={interStateTaxGroups.find(option => option.value === formik.values.interStateTaxGroupId)}
                                                            onChange={(selectedOption) => formik.setFieldValue('interStateTaxGroupId', selectedOption.value)}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-12 col-lg-6 col-md-4 mb-3">
                                                    <div className="d-flex mb-1">
                                                        <label htmlFor="isActive" className="form-label me-3">Active:</label>
                                                    </div>
                                                    <div className="d-flex mb-2">
                                                        <input
                                                            type="radio"
                                                            id="active"
                                                            name="isActive"
                                                            className="form-check-input"
                                                            value={true}
                                                            checked={formik.values.isActive === true}
                                                            onChange={() => formik.setFieldValue("isActive", true)}
                                                        />
                                                        <label className="form-check-label me-3" htmlFor="active">
                                                            Active
                                                        </label>
                                                        <input
                                                            type="radio"
                                                            id="inactive"
                                                            name="isActive"
                                                            className="form-check-input"
                                                            value={false}
                                                            checked={formik.values.isActive === false}
                                                            onChange={() => formik.setFieldValue('isActive', false)}
                                                        />
                                                        <label className="form-check-label" htmlFor="inactive">
                                                            Inactive
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row mt-2">
                                                <div className="col-12">
                                                    <label htmlFor="itemDescription" className="form-label d-flex">
                                                        Description :
                                                    </label>
                                                    <Editor
                                                        value={formik.values.itemDescription}
                                                        tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
                                                        init={{
                                                            height: 300,
                                                            menubar: true,
                                                            plugins: [
                                                                "advlist autolink lists link image charmap print preview anchor",
                                                                "searchreplace visualblocks code fullscreen",
                                                                "insertdatetime media table paste code help wordcount",
                                                            ],
                                                            toolbar:
                                                                "undo redo | formatselect | bold italic backcolor | \
                                                                alignleft aligncenter alignright | bullist numlist outdent indent | \
                                                                link image",
                                                        }}
                                                        onEditorChange={(content) =>
                                                            formik.setFieldValue("itemDescription", content)
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <div className="row mt-3">
                                                <div className="col-12">
                                                    <label htmlFor="termsAndCondition" className="form-label d-flex">
                                                        Terms And Condition :
                                                    </label>
                                                    <Editor
                                                        value={formik.values.termsAndCondition}
                                                        tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
                                                        init={{
                                                            height: 300,
                                                            menubar: true,
                                                            plugins: [
                                                                "advlist autolink lists link image charmap print preview anchor",
                                                                "searchreplace visualblocks code fullscreen",
                                                                "insertdatetime media table paste code help wordcount",
                                                            ],
                                                            toolbar:
                                                                "undo redo | formatselect | bold italic backcolor | \
                                                                alignleft aligncenter alignright | bullist numlist outdent indent | \
                                                                link image",
                                                        }}
                                                        onEditorChange={(content) =>
                                                            formik.setFieldValue("termsAndCondition", content)
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 col-lg-5 col-md-5 mb-3 border border-gray border-3 radius-10 h-75 p-4 m-3">
                                            <div className="row">
                                                <div className="d-flex mb-1">
                                                    <label htmlFor="itemtype" className="form-label me-3"><strong>Type :</strong></label>
                                                </div>
                                                {/* <div className="d-flex">
                                                    <div className="">
                                                        <input
                                                            type="radio"
                                                            id="main"
                                                            name="itemtype"
                                                            className="form-check-input"
                                                            value="main"
                                                            checked={formik.values.itemtype === "main"}
                                                            onChange={handleItemTypeChange}
                                                        />
                                                        <label className="form-check-label me-3" htmlFor="main">Main Type</label>
                                                    </div>
                                                    <div>
                                                        <input
                                                            type="radio"
                                                            id="sub"
                                                            name="itemtype"
                                                            className="form-check-input"
                                                            value="sub"
                                                            checked={formik.values.itemtype === "sub"}
                                                            onChange={handleItemTypeChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="sub">Sub Item</label>
                                                    </div>
                                                </div> */}
                                                <div className="d-flex">
                                                    {Object.values(ItemTypeEnum).map((type) => (
                                                        <div key={type}>
                                                            <input
                                                                type="radio"
                                                                id={type}
                                                                name="itemtype"
                                                                value={type}
                                                                className="form-check-input"
                                                                checked={formik.values.itemtype === type}
                                                                onChange={handleItemTypeChange}
                                                            />
                                                            <label className="form-check-label me-3" htmlFor={type}>{type.charAt(0).toUpperCase() + type.slice(1)} Type</label>
                                                        </div>
                                                    ))}
                                                </div>


                                                {isSubTypeSelected && formik.values.itemtype === ItemTypeEnum.sub && (
                                                    <>
                                                        <div className="mt-3">
                                                            <div className="d-flex flex-wrap gap-5">
                                                                <div className="">
                                                                    {/* <div className="form-check">
                                                                        <input
                                                                            type="radio"
                                                                            id="standard"
                                                                            name="childType"
                                                                            value="standard"
                                                                            checked={formik.values.childType === "standard"}
                                                                            onChange={formik.handleChange}
                                                                            className="form-check-input"
                                                                        />
                                                                        <label className="form-check-label" htmlFor="standard">
                                                                            Standard
                                                                        </label>
                                                                    </div>

                                                                    <div className="form-check">
                                                                        <input
                                                                            type="radio"
                                                                            id="optional"
                                                                            name="childType"
                                                                            value="optional"
                                                                            checked={formik.values.childType === "optional"}
                                                                            onChange={formik.handleChange}
                                                                            className="form-check-input"
                                                                        />
                                                                        <label className="form-check-label" htmlFor="optional">
                                                                            Optional
                                                                        </label>
                                                                    </div> */}
                                                                    {Object.values(ChildTypeEnum).map((childType) => (
                                                                        <div key={childType}>
                                                                            <input
                                                                                type="radio"
                                                                                id={childType}
                                                                                name="childType"
                                                                                value={childType}
                                                                                checked={formik.values.childType === childType}
                                                                                onChange={formik.handleChange}
                                                                                className="form-check-input"
                                                                            />
                                                                            <label className="form-check-label" htmlFor={childType}>
                                                                                {childType.charAt(0).toUpperCase() + childType.slice(1)}
                                                                            </label>
                                                                        </div>
                                                                    ))}
                                                                </div>

                                                                <div className="">
                                                                    {/* <div className="form-check">
                                                                        <input
                                                                            type="radio"
                                                                            id="accessories"
                                                                            name="subchild"
                                                                            value="accessories"
                                                                            checked={formik.values.subchild === "accessories"}
                                                                            onChange={formik.handleChange}
                                                                            className="form-check-input"
                                                                        />
                                                                        <label className="form-check-label" htmlFor="accessories">
                                                                            Accessories
                                                                        </label>
                                                                    </div>

                                                                    <div className="form-check">
                                                                        <input
                                                                            type="radio"
                                                                            id="software"
                                                                            name="subchild"
                                                                            value="software"
                                                                            checked={formik.values.subchild === "software"}
                                                                            onChange={formik.handleChange}
                                                                            className="form-check-input"
                                                                        />
                                                                        <label className="form-check-label" htmlFor="software">
                                                                            Software
                                                                        </label>
                                                                    </div> */}
                                                                    {Object.values(SubChildEnum).map((subchild) => (
                                                                        <div key={subchild}>
                                                                            <input
                                                                                type="radio"
                                                                                id={subchild}
                                                                                name="subchild"
                                                                                value={subchild}
                                                                                checked={formik.values.subchild === subchild}
                                                                                onChange={formik.handleChange}
                                                                                className="form-check-input"
                                                                            />
                                                                            <label className="form-check-label" htmlFor={subchild}>
                                                                                {subchild.charAt(0).toUpperCase() + subchild.slice(1)}
                                                                            </label>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="col-12 mt-3">
                                                            <label htmlFor="parentItemsId" className="form-label d-flex">MainItem:</label>
                                                            <div className="text-start">
                                                                <Select
                                                                    id="parentItemsId"
                                                                    name="parentItemsId"
                                                                    options={parentItemData}
                                                                    value={parentItemData.filter
                                                                        (option => Array.isArray(formik.values.parentItemsId) && formik.values.parentItemsId.includes(option.value))}
                                                                    onChange={(selectedOption) => formik.setFieldValue('parentItemsId', selectedOption ? selectedOption.map((option) => option.value) : [])}
                                                                    placeholder="Select Parent Item"
                                                                    isMulti
                                                                />
                                                            </div>
                                                        </div>
                                                    </>
                                                )}

                                                {!isSubTypeSelected && formik.values.itemtype === "main" && (
                                                    <div className="col-12 mt-3">
                                                        <label htmlFor="categoryId" className="form-label d-flex">Category :</label>
                                                        <div className="text-start">
                                                            <Select
                                                                id="categoryId"
                                                                name="categoryId"
                                                                options={categories}
                                                                value={categories.find(option => option.value === formik.values.categoryId)}
                                                                onChange={(selectedOption) => formik.setFieldValue('categoryId', selectedOption.value)}
                                                                menuPortalTarget={document.body}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="row mt-4">
                                                <div className="col-12">
                                                    <label htmlFor="itemImage" className="form-label d-flex">
                                                        Item Image:
                                                    </label>
                                                    <input
                                                        type="file"
                                                        id="itemImage"
                                                        name="itemImage"
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={handleImageFileChange}
                                                    />

                                                    {previewImageUrl && (
                                                        <div
                                                            style={{
                                                                width: '100%',
                                                                height: '400px',
                                                                border: '1px solid #ddd',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                position: 'relative',
                                                                backgroundColor: '#f8f8f8',
                                                            }}
                                                        >
                                                            <img
                                                                src={previewImageUrl}
                                                                alt="Preview"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                        </div>
                                                    )}

                                                    {!previewImageUrl && (
                                                        <div
                                                            style={{
                                                                width: '100%',
                                                                height: '300px',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                border: '1px dashed #bbb',
                                                                backgroundColor: '#f8f8f8',
                                                                fontSize: '14px',
                                                                color: '#888',
                                                            }}
                                                        >
                                                            <span>Upload Image</span>
                                                        </div>
                                                    )}
                                                    {selectedImage && (
                                                        <div>
                                                            <p>File Name: {selectedImage.name}</p>
                                                            {/* <p>File Size: {Math.round(selectedImage.size / 1024)} KB</p> */}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <hr />
                                            <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                                                    onClick={handleBackClick}
                                                >
                                                    Back
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                                                >
                                                    {isEditMode ? "Update" : "Save"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddItemsComponent;
